import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useRef, useState} from 'react';

import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {AlertDialog} from '../../../../components/common/dialogs/alertDialog';
import {OkCancelDialog} from '../../../../components/common/dialogs/okCancelDialog';
import {isTeamAdmin} from '../../../../helpers/models/accessHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {ChannelGmailModel, ChannelOffice365Model} from '../../../../models/api/channelModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {SettingsInboxesUrl} from '../../../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {getFlatChannels} from '../../../../store/data/channels/channelsSelectors';
import {getAccess} from '../../../../store/data/dataSelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {SettingsFieldLine} from '../../../common/settingsFieldLine';
import {isChannelDenied} from '../../settingsChannelWarningHelpers';
import {
  useAccountSavedEventForChannel,
  useReauthorizeChannel,
  useReportChannelCreationEvent,
} from '../settingsChannelHooks';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 * Props.
 */

interface SettingsReauthorizeEmailFieldProps
  extends SettingsCommonChannelFieldProps<ChannelOffice365Model | ChannelGmailModel> {
  inbox: InboxModel;
  url: SettingsInboxesUrl;
}

/*
 * Component.
 */

export const SettingsReauthorizeEmailField: FC<SettingsReauthorizeEmailFieldProps> = (props) => {
  const userNamespace = useAppSelector(getUserNamespace);
  const access = useAppSelector(getAccess);
  const hasAssumedOAuthFailed = useRef(false);
  const canAuthChannel = useMemo(() => {
    const isOwner = props.edited.namespace === userNamespace;

    return isOwner || isTeamAdmin(props.edited.namespace, access);
  }, [access, props.edited.namespace, userNamespace]);

  const showReauthorize = useMemo(() => {
    const isDenied = isChannelDenied(props.edited);

    return canAuthChannel && !isDenied;
  }, [canAuthChannel, props.edited]);

  const onReauthorizeClick = useReauthorizeChannel(props.edited);

  const [addressToConfirm, setAddressToConfirm] = useState<string | undefined>(undefined);
  const [showExistingAlert, setShowExistingAlert] = useState(false);

  useAccountSavedEventForChannel(props.edited, (account) => {
    // We want to track when our timeout expired, meaning we showed the help
    // banner, but OAuth succeeded. This means our timeout of 1.5 second was
    // too short.
    if (hasAssumedOAuthFailed.current) {
      reportChannelCreationEvent(
        InterfaceEventsEnum.OAUTH_SUCCESS_TIMED_OUT_ERRONEOUSLY,
        props.edited,
        props.inbox,
        {
          accountType: props.edited.type,
        },
      );
    }
    trackInterfaceEvent(InterfaceEventsEnum.OAUTH_ACCOUNT_SAVED, {accountType: account.type});

    // TODO: add the equivalent to `triggerOauthtype` from `front`
    const sameAddress = account.address.toLowerCase() === props.edited.address.toLowerCase();

    if (!sameAddress) {
      reportChannelCreationEvent(
        InterfaceEventsEnum.OAUTH_ACCOUNT_ADDRESS_MISMATCH,
        props.edited,
        props.inbox,
        {
          accountType: props.edited.type,
        },
      );

      setAddressToConfirm(account.address);
    }
  });
  const allChannels = useAppSelector(getFlatChannels);
  const reportChannelCreationEvent = useReportChannelCreationEvent(props.url);

  if (!showReauthorize) {
    return null;
  }

  const maybeRenderDialog = () => {
    if (addressToConfirm && showExistingAlert) {
      return (
        <AlertDialog
          title={<Trans id="addressalreadyexists-title">Address already exists</Trans>}
          onRequestClose={() => {
            setAddressToConfirm(undefined);
            setShowExistingAlert(false);
          }}
        >
          <Trans id="addressalreadyexists-copy">{addressToConfirm} is already in your Front account.</Trans>
        </AlertDialog>
      );
    }

    if (addressToConfirm) {
      return (
        <OkCancelDialog
          title={<Trans id="addressmismatch-title">Address mismatch</Trans>}
          okButtonLabel={<Trans id="addressmismatch-oklabel">Use address</Trans>}
          onOkClick={() => {
            reportChannelCreationEvent(
              InterfaceEventsEnum.OAUTH_ACCOUNT_ADDRESS_MISMATCH_OVERRIDDEN,
              props.edited,
              props.inbox,
              {
                accountType: props.edited.type,
              },
            );

            const alreadyExists = allChannels.some((c) => c.sendAddress === addressToConfirm);

            if (alreadyExists) {
              reportChannelCreationEvent(
                InterfaceEventsEnum.OAUTH_ACCOUNT_ALREADY_EXISTS,
                props.edited,
                props.inbox,
                {
                  accountType: props.edited.type,
                },
              );

              setShowExistingAlert(true);
              return;
            }

            setAddressToConfirm(undefined);
            // TODO: save channel account during creation?
          }}
          onRequestClose={() => {
            reportChannelCreationEvent(
              InterfaceEventsEnum.OAUTH_ACCOUNT_ADDRESS_MISMATCH_CANCELED,
              props.edited,
              props.inbox,
              {
                accountType: props.edited.type,
              },
            );
          }}
        >
          <Trans id="addressmismatch-copy">
            You specified {props.edited.address} as your email address while you gave us access to{' '}
            {addressToConfirm}. Are you sure?
          </Trans>
        </OkCancelDialog>
      );
    }

    return null;
  };

  return (
    <>
      {maybeRenderDialog()}
      <SettingsFieldLine
        title={<Trans id="settingschannel-reauthorize-title">Reauthorize</Trans>}
        subtitle={
          <Trans id="settingschannel-reauthorize-subtitle">
            Update the credentials used to connect this account to Front.
          </Trans>
        }
        content={
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onReauthorizeClick}>
            <Trans id="settingschannel-reauthorize-title">Reauthorize</Trans>
          </Button>
        }
      />
    </>
  );
};
